import { Box, Button, styled, Typography } from '@mui/material';

// Color constants used throughout onboarding
const COLORS = {
  primary: '#2B9FFF',
  primaryDark: '#2481CD',
  secondary: '#667eea',
  secondaryDark: '#5568d3',
  success: '#4caf50',
  successDark: '#3d8b40',
  muted: '#6b7280',
  mutedDark: '#4b5563',
};

// Main card container with glassmorphism effect
export const StepCard = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: '23px',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  width: '100%',
  boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.15)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.up('sm')]: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
  },
}));

// Decorative gradient orb positioned at top-right
export const DecorativeOrb = styled(Box)({
  position: 'absolute',
  top: -100,
  right: -100,
  width: 300,
  height: 300,
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(43, 159, 255, 0.15) 0%, rgba(43, 159, 255, 0) 70%)',
  pointerEvents: 'none',
});

// Gradient title typography
export const GradientTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontSize: '28px',
  lineHeight: 1.2,
  [theme.breakpoints.up('sm')]: {
    fontSize: '34px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '40px',
  },
}));

// Content container with rich text styling for HTML content
export const ContentContainer = styled(Box)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '16px',
  lineHeight: 1.7,
  // Paragraph styling
  '& p': {
    margin: 0,
    marginBottom: theme.spacing(2),
  },
  '& p:last-child': {
    marginBottom: 0,
  },
  // List styling
  '& ul, & ol': {
    margin: 0,
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
  },
  '& li': {
    marginBottom: theme.spacing(1),
  },
  // Link styling
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 0.2s',
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
  // Button-styled links
  '& a[data-style="button"]': {
    display: 'inline-block',
    padding: '8px 22px',
    borderRadius: '100px',
    border: `1px solid ${COLORS.primary}`,
    color: COLORS.primary,
    fontWeight: 500,
    textDecoration: 'none',
    transition: 'all 0.2s',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(43, 159, 255, 0.08)',
      borderColor: COLORS.primaryDark,
      color: COLORS.primaryDark,
    },
    // Color variants
    '&[data-color="primary"]': {
      borderColor: COLORS.primary,
      color: COLORS.primary,
      '&:hover': {
        backgroundColor: 'rgba(43, 159, 255, 0.08)',
        borderColor: COLORS.primaryDark,
        color: COLORS.primaryDark,
      },
    },
    '&[data-color="secondary"]': {
      borderColor: COLORS.secondary,
      color: COLORS.secondary,
      '&:hover': {
        backgroundColor: 'rgba(102, 126, 234, 0.08)',
        borderColor: COLORS.secondaryDark,
        color: COLORS.secondaryDark,
      },
    },
    '&[data-color="success"]': {
      borderColor: COLORS.success,
      color: COLORS.success,
      '&:hover': {
        backgroundColor: 'rgba(76, 175, 80, 0.08)',
        borderColor: COLORS.successDark,
        color: COLORS.successDark,
      },
    },
    '&[data-color="muted"]': {
      borderColor: COLORS.muted,
      color: COLORS.muted,
      '&:hover': {
        backgroundColor: 'rgba(107, 114, 128, 0.08)',
        borderColor: COLORS.mutedDark,
        color: COLORS.mutedDark,
      },
    },
    // Filled variant
    '&[data-variant="filled"]': {
      background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
      color: '#fff',
      border: 'none',
      boxShadow: '0 4px 15px rgba(43, 159, 255, 0.4)',
      '&:hover': {
        background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.secondaryDark} 100%)`,
        boxShadow: '0 6px 20px rgba(43, 159, 255, 0.5)',
        color: '#fff',
      },
    },
  },
  // Strong text
  '& strong': {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  // Image styling
  '& img': {
    borderRadius: '15px',
    maxWidth: '60%',
    maxHeight: '400px',
    width: 'auto',
    height: 'auto',
    display: 'block',
    margin: '0 auto',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
  },
  // Iframe styling
  '& iframe': {
    borderRadius: '18px',
    overflow: 'hidden',
    display: 'block',
    margin: '12px auto',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
}));

// Media image with hover effect
export const MediaImage = styled(Box)({
  width: '100%',
  maxWidth: 720,
  borderRadius: '15px',
  alignSelf: 'center',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}) as typeof Box;

// Video container wrapper
export const VideoContainer = styled(Box)({
  width: '100%',
  maxWidth: 720,
  aspectRatio: '16 / 9',
  borderRadius: '18px',
  overflow: 'hidden',
  alignSelf: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
});

// CTA button styling
export const CtaButton = styled(Button)({
  borderRadius: '100px',
  fontWeight: 500,
  fontSize: '14px',
  padding: '10px 24px',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(43, 159, 255, 0.05)',
  },
});

// Back navigation button
export const BackButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
  fontSize: '14px',
  textTransform: 'none',
  padding: '10px 24px',
  borderRadius: '100px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    color: theme.palette.text.primary,
  },
}));

// Primary gradient button for continue/next actions
export const GradientButton = styled(Button)({
  background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
  color: '#fff',
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none',
  padding: '12px 32px',
  borderRadius: '100px',
  boxShadow: '0 4px 15px rgba(43, 159, 255, 0.4)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.secondaryDark} 100%)`,
    boxShadow: '0 6px 20px rgba(43, 159, 255, 0.5)',
  },
  '&:disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
    boxShadow: 'none',
  },
});
